import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { Loader2 } from 'lucide-preact';
import { assetApi } from '@/api/asset';
import { useToast } from '@/hooks/useToast';
import { useAIChatContextUpdater } from '@/hooks';
import { BRAND_CLASSES } from '@/constants/theme';
import { useAuth } from '@/auth/useAuth';
import { useAssetSignalR } from '@/hooks/useAssetSignalR';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
import { useSasUriRefresh } from '@/hooks/useSasUriRefresh';
import { isWebsterAssetType } from '@/components/webs/constants/assetMeta';
import { AssetReviewTopBar } from './AssetReviewTopBar';
import { AssetDocumentViewer } from './AssetDocumentViewer';
import { AssetReviewSidePanel } from './AssetReviewSidePanel';
import { useAssetReviewPermissions } from '@/hooks/useAssetReviewPermissions';
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview';
import { EmailTemplatePreview } from '@/components/assets/EmailTemplatePreview';
import { EmailCopyPreview } from '@/components/assets/EmailCopyPreview';
export function AssetReviewOverlay({ assets, initialIndex, clientCompanyId, workspaceId, campaignId, onClose, onAssetUpdated, onAssetChange, onEditInWebster, }) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [assetDetail, setAssetDetail] = useState(null);
    const [versions, setVersions] = useState([]);
    const [comments, setComments] = useState([]);
    const [selectedVersionNumber, setSelectedVersionNumber] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [viewerExpanded, setViewerExpanded] = useState(false);
    // LP HTML preview state (fetched from the AssetVersion blob)
    const [lpHtml, setLpHtml] = useState(null);
    const [lpThankYouHtml, setLpThankYouHtml] = useState(null);
    // Past-version HTML for the Email Template/Copy preview (null = preview the current draft live)
    const [emailVersionHtml, setEmailVersionHtml] = useState(null);
    const { showSuccess, showError, showErrorUnless422 } = useToast();
    const { updateContext } = useAIChatContextUpdater();
    const { userProfile } = useAuth();
    const currentAsset = assets[currentIndex];
    // --- SignalR callbacks (stable via refs to avoid dependency cascades) ---
    const currentAssetRef = useRef(currentAsset);
    currentAssetRef.current = currentAsset;
    const onAssetUpdatedRef = useRef(onAssetUpdated);
    onAssetUpdatedRef.current = onAssetUpdated;
    const handleCommentAdded = useCallback((comment) => {
        setComments(prev => prev.some(c => c.CommentId === comment.CommentId) ? prev : [...prev, comment]);
        setAssetDetail(prev => prev ? { ...prev, ETag: comment.ETag } : prev);
    }, []);
    const handleUploadCompleted = useCallback((notification) => {
        showSuccess('Upload complete', `v${notification.versionNumber} is ready.`);
        if (currentAssetRef.current)
            loadAssetDataRef.current(currentAssetRef.current.Id);
        onAssetUpdatedRef.current?.();
    }, [showSuccess]);
    const handleUploadFailed = useCallback((notification) => {
        showError('Upload failed', notification.errorMessage || 'The file could not be processed from cloud storage.');
    }, [showError]);
    const { subscribeToUploadStatus, preJoinForUpload } = useAssetSignalR({
        assetId: currentAsset?.Id,
        userId: userProfile?.Id,
        onCommentAdded: handleCommentAdded,
        onUploadCompleted: handleUploadCompleted,
        onUploadFailed: handleUploadFailed,
    });
    // --- AI chat context ---
    useEffect(() => {
        if (currentAsset) {
            updateContext({
                workspaceId,
                campaignId,
                assetId: currentAsset.Id,
                location: ChatLocations.WebAssetEditor,
                entityType: ChatEntityTypes.Asset,
                entityId: currentAsset.Id,
                viewMode: ChatViewModes.View,
                availableFields: [],
                availableActions: ['CopyToClipboard'],
            });
        }
    }, [workspaceId, campaignId, currentAsset?.Id, updateContext]);
    // --- Data loading ---
    const loadAssetData = useCallback(async (assetId) => {
        setLoading(true);
        setError(undefined);
        setAssetDetail(null);
        setVersions([]);
        setComments([]);
        const [detailRes, versionsRes, commentsRes] = await Promise.all([
            assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId),
            assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId),
            assetApi.getAssetComments(clientCompanyId, workspaceId, campaignId, assetId),
        ]);
        if (detailRes.data) {
            setAssetDetail(detailRes.data);
            setSelectedVersionNumber(detailRes.data.CurrentVersionNumber);
        }
        else if (detailRes.error) {
            setError(detailRes.error);
        }
        if (versionsRes.data) {
            setVersions(versionsRes.data);
            // Re-subscribe for upload result if there's an in-progress URL upload
            const pendingVersion = versionsRes.data.find(v => v.UploadStatus === 'Pending' || v.UploadStatus === 'Downloading');
            if (pendingVersion) {
                subscribeToUploadStatus(pendingVersion.Id);
            }
        }
        if (commentsRes.data) {
            setComments(commentsRes.data);
        }
        setLoading(false);
    }, [clientCompanyId, workspaceId, campaignId, subscribeToUploadStatus]);
    const loadAssetDataRef = useRef(loadAssetData);
    loadAssetDataRef.current = loadAssetData;
    useEffect(() => {
        if (currentAsset) {
            loadAssetDataRef.current(currentAsset.Id);
        }
    }, [currentAsset?.Id]);
    // --- Load LP HTML for promoted Landing Page versions (from AssetVersion blob) ---
    useEffect(() => {
        if (!assetDetail || assetDetail.Type !== 'Landing Page')
            return;
        const version = versions.find(v => v.VersionNumber === selectedVersionNumber);
        const mainBlobUrl = version?.BlobUrl ?? assetDetail.CurrentVersionBlobUrl;
        const thankYouBlobUrl = version?.ThankYouBlobUrl ?? null;
        let cancelled = false;
        // Clear stale HTML before fetching so a missing/expired blob doesn't render the
        // previous version while the user is switching between versions.
        setLpHtml(null);
        setLpThankYouHtml(null);
        if (mainBlobUrl) {
            fetch(mainBlobUrl)
                .then(r => r.ok ? r.text() : null)
                .then(text => {
                if (cancelled)
                    return;
                if (text != null)
                    setLpHtml(text);
            })
                // SAS URI may have expired — refreshSasUris handles recovery.
                .catch(() => { });
        }
        if (thankYouBlobUrl) {
            fetch(thankYouBlobUrl)
                .then(r => r.ok ? r.text() : null)
                .then(text => {
                if (cancelled)
                    return;
                if (text != null)
                    setLpThankYouHtml(text);
            })
                .catch(() => { });
        }
        return () => { cancelled = true; };
    }, [assetDetail?.Type, assetDetail?.CurrentVersionBlobUrl, selectedVersionNumber, versions]);
    // --- Load a past Email Template/Copy version's stored HTML for preview ---
    // The current version previews live (override = null); a past version is rendered through the
    // same substituting preview endpoint by passing its stored HTML as an override (ORB-301).
    useEffect(() => {
        const type = assetDetail?.Type;
        if (type !== 'Email Template' && type !== 'Email Copy')
            return;
        const isCurrentVersion = !assetDetail || selectedVersionNumber === assetDetail.CurrentVersionNumber;
        if (isCurrentVersion) {
            setEmailVersionHtml(null);
            return;
        }
        const version = versions.find(v => v.VersionNumber === selectedVersionNumber);
        // Both email types store the rendered HTML in RenderedHtmlBlobPath (BlobUrl is the design JSON).
        const blobUrl = version?.RenderedHtmlBlobUrl;
        let cancelled = false;
        setEmailVersionHtml(null);
        if (blobUrl) {
            fetch(blobUrl)
                .then(r => r.ok ? r.text() : null)
                .then(text => { if (!cancelled && text != null)
                setEmailVersionHtml(text); })
                .catch(err => {
                // A lapsed SAS URI surfaces as a fetch failure here; useSasUriRefresh re-fetches the
                // versions with fresh URIs, so this is recoverable — log at debug rather than swallow.
                console.debug('[AssetReview] version preview HTML fetch failed (SAS may have expired)', err);
            });
        }
        return () => { cancelled = true; };
    }, [assetDetail?.Type, assetDetail?.CurrentVersionNumber, selectedVersionNumber, versions]);
    // --- SAS URI refresh (silent — no loading state reset) ---
    const refreshSasUris = useCallback(async () => {
        if (!currentAsset)
            return;
        const [detailRes, versionsRes] = await Promise.all([
            assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, currentAsset.Id),
            assetApi.getAssetVersions(clientCompanyId, workspaceId, currentAsset.Id),
        ]);
        if (detailRes.data) {
            setAssetDetail(prev => prev ? { ...prev, ...detailRes.data } : detailRes.data);
        }
        if (versionsRes.data) {
            setVersions(versionsRes.data);
        }
    }, [clientCompanyId, workspaceId, campaignId, currentAsset?.Id]);
    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    // Lock body scroll + hide AI chat widget
    useBodyScrollLock();
    // --- Handlers ---
    const handlePrevious = currentIndex > 0
        ? () => {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            onAssetChange?.(assets[newIndex].Id);
        }
        : null;
    const handleNext = currentIndex < assets.length - 1
        ? () => {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            onAssetChange?.(assets[newIndex].Id);
        }
        : null;
    const handleVersionChange = (versionNumber) => {
        setSelectedVersionNumber(versionNumber);
    };
    const handleToggleExpand = useCallback(() => {
        setViewerExpanded(prev => !prev);
    }, []);
    const handleSignOff = async (comment) => {
        if (!currentAsset || !assetDetail)
            return;
        const dto = {
            ETag: assetDetail.ETag,
            WorkspaceId: workspaceId,
            Confirmed: true,
            SignOffComment: comment,
        };
        const response = await assetApi.signOff(clientCompanyId, workspaceId, campaignId, currentAsset.Id, dto);
        if (response.error) {
            showErrorUnless422('Sign-off failed', response);
            return;
        }
        if (response.data) {
            setAssetDetail(response.data);
            showSuccess('Signed off', `${response.data.Name} has been signed off.`);
            onAssetUpdated?.();
        }
    };
    const handleAddComment = async (text, parentCommentId) => {
        if (!currentAsset || !assetDetail)
            return;
        const response = await assetApi.addComment(clientCompanyId, workspaceId, campaignId, currentAsset.Id, {
            ETag: assetDetail.ETag,
            WorkspaceId: workspaceId,
            Text: text,
            VersionNumber: selectedVersionNumber,
            ParentCommentId: parentCommentId,
        });
        if (response.error) {
            showErrorUnless422('Comment failed', response);
            return;
        }
        if (response.data) {
            setComments(prev => prev.some(c => c.CommentId === response.data.CommentId) ? prev : [...prev, response.data]);
            setAssetDetail(prev => prev ? { ...prev, ETag: response.data.ETag } : prev);
        }
    };
    const handleUploadVersion = async (file, versionComment) => {
        if (!currentAsset || !assetDetail)
            return;
        const response = await assetApi.uploadVersion(clientCompanyId, workspaceId, currentAsset.Id, {
            ETag: assetDetail.ETag,
            AssetId: currentAsset.Id,
            CampaignId: campaignId,
            WorkspaceId: workspaceId,
            FileName: file.name,
            MimeType: file.type || 'application/octet-stream',
            FileSizeBytes: file.size,
            VersionComment: versionComment,
        }, file);
        if (response.error) {
            showErrorUnless422('Upload failed', response);
            return;
        }
        if (response.data) {
            setVersions(prev => [...prev, response.data]);
            setSelectedVersionNumber(response.data.VersionNumber);
            if (response.data.ParentAssetETag) {
                setAssetDetail(prev => prev ? { ...prev, ETag: response.data.ParentAssetETag } : prev);
            }
            showSuccess('Version uploaded', `v${response.data.VersionNumber} has been uploaded.`);
            onAssetUpdated?.();
        }
    };
    const handleDeleteAsset = async () => {
        if (!currentAsset)
            return;
        const response = await assetApi.deleteAsset(clientCompanyId, workspaceId, campaignId, currentAsset.Id);
        if (response.error) {
            showErrorUnless422('Delete failed', response);
            throw response.error;
        }
        showSuccess('Asset deleted', `${currentAsset.Name} has been deleted.`);
        onAssetUpdated?.();
        onClose();
    };
    const handleUploadVersionByCloudStorage = async (fileId, fileName, mimeType, provider, versionComment) => {
        if (!currentAsset || !assetDetail || !userProfile?.Id)
            return;
        // Pre-join SignalR group BEFORE the upload API call to avoid missing
        // fast completion notifications (race condition prevention)
        const cleanup = await preJoinForUpload('');
        const response = await assetApi.uploadVersionByCloudStorage(clientCompanyId, workspaceId, currentAsset.Id, {
            ETag: assetDetail.ETag,
            AssetId: currentAsset.Id,
            CampaignId: campaignId,
            WorkspaceId: workspaceId,
            CloudStorageFileId: fileId,
            CloudStorageFileName: fileName,
            CloudStorageMimeType: mimeType,
            CloudStorageProvider: provider,
            VersionComment: versionComment,
        });
        if (response.error) {
            cleanup(); // Clean up SignalR subscription on failure
            showErrorUnless422('Upload failed', response);
            return;
        }
        if (response.data) {
            setVersions(prev => [...prev, response.data]);
            setSelectedVersionNumber(response.data.VersionNumber);
            if (response.data.ParentAssetETag) {
                setAssetDetail(prev => prev ? { ...prev, ETag: response.data.ParentAssetETag } : prev);
            }
            // Now subscribe for this specific version's upload status
            subscribeToUploadStatus(response.data.Id);
        }
    };
    const handleUpdateAsset = async (updates) => {
        if (!currentAsset || !assetDetail)
            return;
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, currentAsset.Id, {
            Id: currentAsset.Id,
            ETag: assetDetail.ETag,
            CampaignId: campaignId,
            ...updates,
        });
        if (response.status === 409) {
            showError('Conflict', 'This asset was modified elsewhere. Refreshing...');
            await loadAssetData(currentAsset.Id);
            throw new Error('ETag conflict');
        }
        if (response.error) {
            showErrorUnless422('Update failed', response);
            throw new Error(response.error);
        }
        if (response.data) {
            // Merge metadata but preserve existing SAS URIs to avoid unnecessary viewer reloads
            setAssetDetail(prev => {
                if (!prev)
                    return response.data;
                return {
                    ...response.data,
                    CurrentVersionBlobUrl: prev.CurrentVersionBlobUrl,
                    CurrentVersionBlobUrlExpiresInSeconds: prev.CurrentVersionBlobUrlExpiresInSeconds,
                };
            });
            const label = updates.Status ? `Status changed to ${response.data.Status}` : `Name updated`;
            showSuccess('Asset updated', label);
            onAssetUpdated?.();
        }
    };
    const permissions = useAssetReviewPermissions();
    const canEditInWebster = permissions.canEditInWebster && assetDetail && isWebsterAssetType(assetDetail.Type) && !!onEditInWebster;
    const handleEditInWebster = useCallback(() => {
        if (assetDetail && onEditInWebster) {
            onEditInWebster(assetDetail.Id, assetDetail.Type);
        }
    }, [assetDetail, onEditInWebster]);
    // Determine which BlobUrl/fileName to show based on selected version
    const selectedVersion = versions.find(v => v.VersionNumber === selectedVersionNumber);
    const viewerBlobUrl = selectedVersion?.BlobUrl ?? assetDetail?.CurrentVersionBlobUrl ?? null;
    const viewerFileName = selectedVersion?.OriginalFileName ?? assetDetail?.CurrentVersionFileName ?? null;
    const viewerMimeType = selectedVersion?.MimeType ?? null;
    // --- SAS URI auto-refresh before expiry ---
    const selectedVersionExpiry = selectedVersion?.BlobUrlExpiresInSeconds;
    const detailExpiry = assetDetail?.CurrentVersionBlobUrlExpiresInSeconds;
    const shortestExpiry = [selectedVersionExpiry, detailExpiry]
        .filter((v) => v != null && v > 0)
        .reduce((min, v) => Math.min(min, v), Infinity);
    useSasUriRefresh(shortestExpiry === Infinity ? null : shortestExpiry, refreshSasUris);
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900", children: [_jsx(AssetReviewTopBar, { onClose: onClose, onPrevious: handlePrevious, onNext: handleNext, currentIndex: currentIndex, totalAssets: assets.length, onEditInWebster: canEditInWebster ? handleEditInWebster : undefined }), error ? (_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-sm text-red-600 dark:text-red-400 mb-2", children: error }), _jsx("button", { onClick: () => currentAsset && loadAssetData(currentAsset.Id), className: "text-sm text-primary-600 dark:text-primary-400 hover:underline", children: "Try again" })] }) })) : loading && !assetDetail ? (_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsx(Loader2, { className: "w-8 h-8 text-slate-400 animate-spin" }) })) : assetDetail?.Status === 'Draft' ? (_jsx("div", { className: "flex-1 flex items-center justify-center px-6", children: _jsxs("div", { className: "text-center max-w-md", children: [_jsx("p", { className: "text-sm text-slate-600 dark:text-slate-300 mb-4", children: "This asset is still being drafted and isn't available for review yet." }), canEditInWebster && (_jsx("button", { onClick: handleEditInWebster, className: `inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${BRAND_CLASSES.BUTTON.PRIMARY}`, children: "Open in builder" }))] }) })) : (_jsxs("div", { className: "flex-1 flex overflow-hidden", children: [_jsx("div", { className: "flex-1 min-w-0", children: assetDetail?.Type === 'Landing Page' ? (_jsx(HtmlPreview, { html: lpHtml, thankYouHtml: lpThankYouHtml })) : assetDetail?.Type === 'Email Template' ? (
                        // Server-rendered iframe instead of the editable Tiptap canvas — mirrors the
                        // Email Copy preview pane so what reviewers see here matches what the recipient
                        // gets in their inbox (table-based centering, sandboxed HTML, no editor quirks).
                        _jsx(EmailTemplatePreview, { clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, templateAssetId: assetDetail.Id, htmlOverride: emailVersionHtml })) : assetDetail?.Type === 'Email Copy' ? (
                        // Read-only server-rendered preview — matches the live editor's preview
                        // pane so reviewers see exactly what the recipient gets. Renders the current
                        // draft live, or a selected past version's stored HTML (emailVersionHtml).
                        _jsx(EmailCopyPreview, { clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, assetId: assetDetail.Id, subject: assetDetail.Subject, htmlOverride: emailVersionHtml })) : (_jsx(AssetDocumentViewer, { blobUrl: viewerBlobUrl, fileName: viewerFileName, mimeType: viewerMimeType, expanded: viewerExpanded, onToggleExpand: handleToggleExpand })) }), !viewerExpanded && _jsx("div", { className: "w-[400px] xl:w-[440px] flex-shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900", children: _jsx(AssetReviewSidePanel, { assetDetail: assetDetail, versions: versions, comments: comments, selectedVersionNumber: selectedVersionNumber, onVersionChange: handleVersionChange, onSignOff: handleSignOff, onAddComment: handleAddComment, onUploadVersion: handleUploadVersion, onUploadVersionByCloudStorage: handleUploadVersionByCloudStorage, onStatusChange: (status) => handleUpdateAsset({ Status: status }), onNameChange: (name) => handleUpdateAsset({ Name: name }), onDeleteAsset: handleDeleteAsset, loading: loading, permissions: permissions }) })] }))] }));
}
